/**
 * Auswertungen über alle Fassungen eines Berichts (seit 07.10.2026, Vorbild
 * der Kundenbericht für Weiterbildung Now, Entwurf „99takes Report-Template"
 * in Claude Design).
 *
 * Alles hier rechnet nur mit dem, was die Nutzlast schon trägt: Noten je
 * Persona (`personen`), Einwände (`blockers`) und die Texte. Jobs, Profile und
 * Reaktionstexte stehen bewusst nicht im Bericht (report.ts, „Was nicht in die
 * Nutzlast gehört", bestätigt von Robert am 07.10.2026). Was nur aus ihnen
 * käme, schreibt der Autor über publish_report (`narrative.kernpunkte`,
 * `hebel`, `empfehlungen`).
 *
 * Die Nutzlast kommt aus JSON, nicht aus einem Typ: kaputte Zeilen fallen
 * einzeln heraus, statt die Seite zu sprengen.
 */

import { personaNoten, type PersonaNote } from './bericht-noten';
import { fassungsname } from './fassungsname';
import { locale, t } from './sprache';

// ─── Persona × Fassung ───────────────────────────────────────────────────────

export interface MatrixZeile {
  persona: string;
  rand: boolean;
  /** Eine Zelle je Fassung, `null`, wo die Persona in dieser Fassung nicht urteilte. */
  zellen: (PersonaNote | null)[];
}

export interface Matrix {
  spalten: { kurz: string; name: string }[];
  zeilen: MatrixZeile[];
}

/**
 * Je Persona der Abstand zur Vergleichsfassung in jeder Fassung. Nur
 * Vergleiche mit Noten; Feedback-Runs haben keine. Sortiert wie
 * `personaNoten` in der letzten Fassung: Kern vor Rand, wer am stärksten
 * anzieht zuerst. Personas, die in der letzten Fassung fehlen, stehen hinten.
 */
export function fassungsMatrix(varianten: any[]): Matrix {
  const vergleiche = (varianten ?? []).filter((v) => v?.mode !== 'feedback' && personaNoten(v).length > 0);
  const spalten = vergleiche.map((v, i) => ({ kurz: `F${i + 1}`, name: fassungsname(v?.label) || t(`Fassung ${i + 1}`, `Version ${i + 1}`) }));
  const je = vergleiche.map((v) => new Map(personaNoten(v).map((p) => [p.persona, p])));
  const reihenfolge: string[] = [];
  for (let i = je.length - 1; i >= 0; i--) {
    for (const name of je[i]!.keys()) if (!reihenfolge.includes(name)) reihenfolge.push(name);
  }
  const zeilen = reihenfolge.map((persona) => {
    const zellen = je.map((m) => m.get(persona) ?? null);
    return { persona, rand: zellen.some((z) => z?.rolle === 'rand'), zellen };
  });
  // Stabil: Kern vor Rand, sonst die Reihenfolge der letzten Fassung.
  zeilen.sort((a, b) => Number(a.rand) - Number(b.rand));
  return { spalten, zeilen };
}

/** Farbstufe eines Abstands. Unter 0,3 Punkten gilt er als knapp. */
export type Stufe = 'stark-minus' | 'minus' | 'knapp' | 'plus' | 'stark-plus';
export function stufe(delta: number): Stufe {
  if (delta <= -1) return 'stark-minus';
  if (delta <= -0.3) return 'minus';
  if (delta < 0.3) return 'knapp';
  if (delta < 1) return 'plus';
  return 'stark-plus';
}

// ─── Was bremst ──────────────────────────────────────────────────────────────

const FUELLWOERTER = new Set(['nicht', 'nichts', 'wenn', 'oder', 'eine', 'einen', 'einer', 'statt', 'auch', 'kein', 'keine',
  'nach', 'über', 'ueber', 'unter', 'dass', 'wird', 'sind', 'wie', 'was', 'with', 'from', 'that', 'what', 'without',
  'fehlt', 'fehlen', 'seite', 'text', 'mein', 'meine', 'meinen', 'mich', 'mir', 'ich', 'sehe', 'steht', 'nirgends',
  'this', 'there', 'have', 'does', 'page']);

/** Wortstämme wie in analyse.ts: Wörter ab vier Buchstaben, die ersten sechs, ohne Füllwörter. */
export function staemme(s: string): Set<string> {
  return new Set(String(s).toLowerCase().split(/[^a-zäöüß0-9]+/)
    .filter((w) => w.length >= 4 && !FUELLWOERTER.has(w)).map((w) => w.slice(0, 6)));
}

export interface Einwandgruppe {
  /** Der Einwand, der die Gruppe vertritt: aus der jüngsten Fassung, sonst der erste. */
  einwand: string;
  /** Verschiedene Personas, die ihn nennen. */
  personas: string[];
  /** Kurznamen der Fassungen, in denen er vorkommt (F1, F2, …). */
  offenIn: string[];
  /** Nur Randpersonas nennen ihn. */
  nurRand: boolean;
}

/**
 * Einwände über alle Fassungen nach Bedeutung gebündelt, ohne Modell. Zwei
 * Einwände gehören zusammen, wenn sie mindestens zwei Stämme teilen und das
 * mindestens ein Drittel der Stämme des kürzeren ist (einfache Verkettung).
 * Ganze Sätze teilen weniger Wörter als die Stichworte in analyse.ts, darum
 * der Überlappungs- statt des Jaccard-Maßes. Gezählt werden Personas, nicht
 * Nennungen. Gleiche Eingabe, gleiche Gruppen.
 */
export function buendleEinwaende(varianten: any[]): Einwandgruppe[] {
  const eintraege: { einwand: string; persona: string; fassung: number; rand: boolean; st: Set<string> }[] = [];
  (varianten ?? []).forEach((v, i) => {
    for (const b of Array.isArray(v?.blockers) ? v.blockers : []) {
      const einwand = typeof b?.einwand === 'string' ? b.einwand.trim() : '';
      const persona = typeof b?.persona === 'string' ? b.persona : '';
      if (!einwand || !persona) continue;
      eintraege.push({ einwand, persona, fassung: i, rand: b.rolle === 'rand', st: staemme(einwand) });
    }
  });
  const gruppe = eintraege.map((_, i) => i);
  const wurzel = (i: number): number => (gruppe[i] === i ? i : (gruppe[i] = wurzel(gruppe[i]!)));
  for (let i = 0; i < eintraege.length; i++) {
    for (let j = i + 1; j < eintraege.length; j++) {
      const a = eintraege[i]!.st, b = eintraege[j]!.st;
      if (!a.size || !b.size) continue;
      let gleich = 0;
      for (const w of a) if (b.has(w)) gleich++;
      if (gleich >= 2 && gleich / Math.min(a.size, b.size) >= 1 / 3) gruppe[wurzel(j)] = wurzel(i);
    }
  }
  const gruppen = new Map<number, typeof eintraege>();
  eintraege.forEach((e, i) => { const r = wurzel(i); gruppen.set(r, [...(gruppen.get(r) ?? []), e]); });
  return [...gruppen.values()].map((g) => {
    const juengste = Math.max(...g.map((e) => e.fassung));
    const vertreter = g.filter((e) => e.fassung === juengste).sort((a, b) => a.einwand.length - b.einwand.length)[0];
    const personas = [...new Set(g.map((e) => e.persona))];
    const fassungen = [...new Set(g.map((e) => e.fassung))].sort((a, b) => a - b);
    return {
      einwand: vertreter!.einwand,
      personas,
      offenIn: fassungen.map((f) => `F${f + 1}`),
      nurRand: g.every((e) => e.rand),
    };
  }).sort((a, b) => Number(a.nurRand) - Number(b.nurRand) || b.personas.length - a.personas.length
    || b.offenIn.length - a.offenIn.length || Number(b.offenIn.at(-1)?.slice(1)) - Number(a.offenIn.at(-1)?.slice(1)));
}

/**
 * Der Name einer Persona ohne Rolle und Größe. Die Nutzlast hängt sie an den
 * Namen an (`personaLabel` in engine.ts: „Name, Rolle, Größe"), und weil Namen
 * selbst Kommas tragen („Director Web Platform, Versicherung auf AEM"), lässt
 * sich das nicht am Komma trennen. Gesucht wird der längste Name aus der
 * Zielgruppe, mit dem das Label beginnt; ohne Treffer bleibt das Label.
 */
export function kurzname(label: string, namen: string[]): string {
  let bester = '';
  for (const n of namen) {
    if (n && n.length > bester.length && (label === n || label.startsWith(`${n},`))) bester = n;
  }
  return bester || label;
}

// ─── Wer skeptisch bleibt ────────────────────────────────────────────────────

export interface Skeptiker {
  persona: string;
  geprueft: number;
  vergleich: number;
  einwand: string | null;
  unklar: string | null;
}

/**
 * Kernpersonas, die auch die letzte Fassung nicht abholt: sie ziehen die
 * Vergleichsfassung vor (oder bewerten sie höher), die niedrigste Note zuerst.
 * Dazu ihr Einwand aus derselben Fassung. Höchstens drei.
 */
export function skeptiker(varianten: any[], max = 3): Skeptiker[] {
  const letzte = [...(varianten ?? [])].reverse().find((v) => v?.mode !== 'feedback' && personaNoten(v).length > 0);
  if (!letzte) return [];
  const einwaende = new Map<string, any>();
  for (const b of Array.isArray(letzte.blockers) ? letzte.blockers : []) {
    if (typeof b?.persona === 'string' && !einwaende.has(b.persona)) einwaende.set(b.persona, b);
  }
  return personaNoten(letzte)
    .filter((p) => p.rolle !== 'rand' && (p.gewinner === 'vergleich' || (p.gewinner == null && p.delta < 0)))
    .sort((a, b) => a.geprueft - b.geprueft || a.delta - b.delta)
    .slice(0, max)
    .map((p) => {
      const b = einwaende.get(p.persona);
      return {
        persona: p.persona,
        geprueft: p.geprueft,
        vergleich: p.vergleich,
        einwand: typeof b?.einwand === 'string' ? b.einwand : null,
        unklar: typeof b?.unklar === 'string' && b.unklar ? b.unklar : null,
      };
    });
}

// ─── Fassungen lesen ─────────────────────────────────────────────────────────

export interface Abschnitt {
  /** Rolle oder Überschrift, wie sie im Text steht (ohne #). Leer für den Anfang ohne Überschrift. */
  kopf: string;
  rumpf: string;
}

/** `HEADLINE: …`, `TEXT: …` aus dem Seitentest (fassung_b) und Markdown-Überschriften beginnen einen Abschnitt. */
const PRAEFIX = /^([A-ZÄÖÜ][A-ZÄÖÜ_ -]{2,30}):\s*(.*)$/;
const UEBERSCHRIFT = /^#{1,4}\s+(.+)$/;

/**
 * Teilt einen Text in Abschnitte: an Markdown-Überschriften und an den
 * Präfixen, mit denen der Seitentest Fassungen schreibt. Ohne beides ein
 * einziger Abschnitt. Der Text bleibt wörtlich; nur Leerzeilen am Rand fallen.
 */
export function abschnitte(text: unknown): Abschnitt[] {
  const zeilen = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  const aus: Abschnitt[] = [];
  let aktuell: Abschnitt = { kopf: '', rumpf: '' };
  const schliessen = () => {
    aktuell.rumpf = aktuell.rumpf.replace(/^\n+|\n+$/g, '');
    if (aktuell.kopf || aktuell.rumpf) aus.push(aktuell);
  };
  for (const z of zeilen) {
    const u = z.match(UEBERSCHRIFT);
    const p = u ? null : z.match(PRAEFIX);
    if (u) { schliessen(); aktuell = { kopf: u[1]!.trim(), rumpf: '' }; continue; }
    if (p) { schliessen(); aktuell = { kopf: p[1]!.trim(), rumpf: p[2] ?? '' }; continue; }
    aktuell.rumpf += (aktuell.rumpf ? '\n' : '') + z;
  }
  schliessen();
  return aus;
}

export type Aenderung = 'gleich' | 'umformuliert' | 'neu' | 'gestrichen';

export interface AbschnittVergleich {
  kopf: string;
  aenderung: Aenderung;
  neu: Abschnitt | null;
  alt: Abschnitt | null;
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-zäöüß0-9]+/g, ' ').trim();

/**
 * Abschnitte der geprüften Fassung neben denen der Vergleichsfassung. Gepaart
 * wird über die Überschrift: gleich, ähnlich (die Hälfte der Stämme gleich)
 * oder an derselben Stelle ohne Überschrift. Was nur in der Vergleichsfassung
 * steht, ist „gestrichen" und steht an der Stelle, an der es dort stand.
 */
export function vergleicheAbschnitte(neuText: unknown, altText: unknown): AbschnittVergleich[] {
  const neu = abschnitte(neuText);
  const alt = abschnitte(altText);
  const vergeben = new Set<number>();
  const passt = (a: Abschnitt, b: Abschnitt) => {
    if (norm(a.kopf) === norm(b.kopf)) return true;
    const x = staemme(a.kopf), y = staemme(b.kopf);
    if (!x.size || !y.size) return false;
    let gleich = 0;
    for (const w of x) if (y.has(w)) gleich++;
    return gleich / Math.min(x.size, y.size) >= 0.5;
  };
  const paare = neu.map((n, i) => {
    let j = alt.findIndex((a, k) => !vergeben.has(k) && passt(n, a));
    if (j < 0 && !n.kopf && alt[i] && !alt[i]!.kopf && !vergeben.has(i)) j = i;
    if (j >= 0) vergeben.add(j);
    return { n, j };
  });
  const aus: AbschnittVergleich[] = [];
  let naechsterAlt = 0;
  for (const { n, j } of paare) {
    // Gestrichene Abschnitte vor dem gepaarten an ihrer alten Stelle einreihen.
    if (j >= 0) {
      for (; naechsterAlt < j; naechsterAlt++) {
        if (!vergeben.has(naechsterAlt)) aus.push({ kopf: alt[naechsterAlt]!.kopf, aenderung: 'gestrichen', neu: null, alt: alt[naechsterAlt]! });
      }
      naechsterAlt = Math.max(naechsterAlt, j + 1);
      const a = alt[j]!;
      const gleich = norm(a.kopf + ' ' + a.rumpf) === norm(n.kopf + ' ' + n.rumpf);
      aus.push({ kopf: n.kopf || a.kopf, aenderung: gleich ? 'gleich' : 'umformuliert', neu: n, alt: a });
    } else {
      aus.push({ kopf: n.kopf, aenderung: 'neu', neu: n, alt: null });
    }
  }
  for (; naechsterAlt < alt.length; naechsterAlt++) {
    if (!vergeben.has(naechsterAlt)) aus.push({ kopf: alt[naechsterAlt]!.kopf, aenderung: 'gestrichen', neu: null, alt: alt[naechsterAlt]! });
  }
  return aus;
}

/** Zeichen eines Textes, deutsch formatiert („11.927 Zeichen"), englisch „11,927 characters". */
export function zeichenZeile(text: unknown): string {
  const n = String(text ?? '').length;
  return t(`${new Intl.NumberFormat(locale()).format(n)} Zeichen`, `${new Intl.NumberFormat(locale()).format(n)} ${n === 1 ? 'character' : 'characters'}`);
}
